import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation, Link, NavLink, useNavigate } from "react-router-dom";
import { Navbar, Container, Dropdown, Nav } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import logo from "../assets/images/New_Logo.png";
import CustomFooter from "../components/Footer";
import { FavoriteContext } from "../context/FavoriteContext";
import { CartContext } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import { FaCartShopping, FaRightFromBracket } from "react-icons/fa6";
import "../style/responsive.css";
import { FaBars, FaSignInAlt, FaUser, FaUserPlus } from "react-icons/fa";
import axiosInstance from "../utils/axiosConfig";

const englishFlag = "https://res.cloudinary.com/dwtfz4jav/image/upload/v1762350443/english-flag_fgkznr.png";
const cambodiaFlag = "https://res.cloudinary.com/dwtfz4jav/image/upload/v1762350429/cambodia-flag_kkh5pq.png";

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { favorites } = useContext(FavoriteContext);
  const { cart } = useContext(CartContext);
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const storedProfilePicture = localStorage.getItem("profilePicture");

  const hideNavbarPaths = ["/login", "/signup", "/forgot-password"];
  const shouldShowNavbar = !hideNavbarPaths.includes(location.pathname);

  const hideFooterPaths = ["/login", "/signup", "/forgot-password"];
  const shouldShowFooter = !hideFooterPaths.includes(location.pathname);
  const [selectedFlag, setSelectedFlag] = useState(cambodiaFlag);
  const [deliveryLimit, setDeliveryLimit] = useState(null);

  // State for navbar collapse
  const [expanded, setExpanded] = useState(false);
  const navbarRef = useRef(null);

  // Close navbar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchDeliveryLimit = async () => {
      const cacheKey = "cachedDeliveryLimit";
      const cacheTimestampKey = "cachedDeliveryLimitTimestamp";

      // Check cache first
      const cachedLimit = localStorage.getItem(cacheKey);
      if (cachedLimit) {
        setDeliveryLimit(JSON.parse(cachedLimit));
      }

      try {
        // Always fetch fresh data in background
        const res = await axiosInstance.get("/delivery-free-limits");
        const limits = res.data;

        if (Array.isArray(limits) && limits.length > 0) {
          setDeliveryLimit(limits[0].amount);
          // Update cache with fresh data
          localStorage.setItem(cacheKey, JSON.stringify(limits[0].amount));
          localStorage.setItem(cacheTimestampKey, Date.now().toString());
        }
      } catch (error) {
        console.error("Error fetching delivery limit:", error);
        // If no cache exists and API fails, set to null
        if (!cachedLimit) {
          setDeliveryLimit(null);
        }
      }
    };

    fetchDeliveryLimit();
  }, []);

  const handleLogout = () => {
    logout();
    // Clear any remaining profile picture cache
    localStorage.removeItem('profilePicture');
    // Navigate to login page
    navigate('/login', { replace: true });
    setExpanded(false);
  };

  // Force Khmer language as default on component mount
  useEffect(() => {
    const currentLanguage = i18n.language;

    // If current language is not Khmer, change it to Khmer
    if (currentLanguage !== 'km') {
      i18n.changeLanguage('km');
      setSelectedFlag(cambodiaFlag);
    }
  }, [i18n]);


  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setSelectedFlag(lng === "en" ? englishFlag : cambodiaFlag);
  };


  return (
    <>
      {shouldShowNavbar && (
        <section className="layout-file section-header" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
          <Navbar bg="dark" variant="dark" expand="lg">
            <Container fluid className="px-4 fixed-header">
              <Navbar.Brand>
                <div className="d-flex align-items-center py-1">
                  <small className="text-white xs-text-size">
                    {deliveryLimit != null
                      ? t('FREE_SHIPPING', { amount: deliveryLimit })
                      : t('FREE_SHIPPING')
                    }
                  </small>
                </div>
              </Navbar.Brand>
              <div className="d-flex gap-3">
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="link"
                    className="text-decoration-none custom-dropdown-toggle d-flex align-items-center gap-1 px-0"
                  >
                    <img
                      src={selectedFlag}
                      alt="Language"
                      style={{ width: "20px" }}
                    />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => changeLanguage("en")}>
                      <img
                        src={englishFlag}
                        alt="English"
                        style={{ width: "20px", marginRight: "8px" }}
                      />
                      <small className="xs-text-dropdown">{t("ENGLISH")}</small>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => changeLanguage("km")}>
                      <img
                        src={cambodiaFlag}
                        alt="Khmer"
                        style={{ width: "20px", marginRight: "8px" }}
                      />
                      <small className="xs-text-dropdown">{t("KHMER")}</small>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <div className="line-style"></div>
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="link"
                    className="text-decoration-none custom-dropdown-toggle d-flex align-items-center gap-1 px-0 border-0"
                    id="dropdown-user-menu"
                  >
                    {storedProfilePicture ? (
                      <img
                        src={storedProfilePicture}
                        alt="Profile"
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          boxShadow: "0 0 0 1px #fff, 0 0 6px rgba(0,0,0,0.35)"
                        }}
                      />
                    ) : (
                      <FaUser className="text-white" />
                    )}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {!isAuthenticated ? (
                      <>
                        <Dropdown.Item as={Link} to="/login" onClick={() => setExpanded(false)}>
                          <FaSignInAlt className="me-2"></FaSignInAlt>
                          <small className="xs-text-dropdown">{t('LOGIN')}</small>
                        </Dropdown.Item>
                        <Dropdown.Item as={Link} to="/signup" onClick={() => setExpanded(false)}>
                          <FaUserPlus className="me-2"></FaUserPlus>
                          <small className="xs-text-dropdown">{t('SIGN_UP')}</small>
                        </Dropdown.Item>
                      </>
                    ) : (
                      <>
                        <Dropdown.Item as={Link} to="/profile" onClick={() => setExpanded(false)}>
                          <i className="fa-regular fa-user me-3 xs-text-dropdown"></i>
                          <small className="xs-text-dropdown">{user?.fullName || 'User'}</small>
                        </Dropdown.Item>
                        <Dropdown.Item as={Link} to="/order-history" onClick={() => setExpanded(false)}>
                          <i className="fa-solid fa-cart-shopping me-3 xs-text-dropdown"></i>
                          <small className="xs-text-dropdown">{t('Order History')}</small>
                        </Dropdown.Item>
                        <Dropdown.Item onClick={handleLogout}>
                          <FaRightFromBracket className="me-3 text-danger xs-text-dropdown" />
                          <small className="text-danger xs-text-dropdown">{t('LOG_OUT')}</small>
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Container>
          </Navbar>

          <Navbar
            bg="transparent"
            variant="dark"
            expand="md"
            className="z-100 mt-md-4 mt-0 main-navigation d-flex justify-content-between align-items-center"
            ref={navbarRef}
            expanded={expanded}
            onToggle={setExpanded}
          >
            <Container fluid className="px-4 fixed-top z-2 mt-top menu-height">

              {/* Logo on the left */}
              <Navbar.Brand>
                <Link to="/home" className="text-decoration-none text-white" onClick={() => setExpanded(false)}>
                  <img src={logo} alt="logo" className="logo-img" />
                </Link>
              </Navbar.Brand>

              {/* Collapsible menu in the center */}
              <Navbar.Collapse id="basic-navbar-nav" className="bg-dark-custom px-4 order-2 order-md-1">
                <Nav className="mx-auto gap-4 menu-bar">
                  {[
                    { to: "/home", label: t('HOME') },
                    { to: "/men-perfume", label: t('MEN_PERFUME') },
                    { to: "/women-perfume", label: t('WOMEN_PERFUME') },
                    { to: "/unisex", label: t('UNISEX') },
                    { to: "/blogs", label: t('BLOG') },
                    { to: "/contact", label: t('CONTACT') },
                  ].map(({ to, label }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        `nav-link text-color me-2 px-0 ${isActive ? "active-nav-link" : ""}`
                      }
                      onClick={() => {
                        setExpanded(false);
                        if (window.innerWidth < 768) {
                          navigate(to);
                        } else {
                          navigate(to);
                        }
                      }}
                    >
                      {label}
                    </NavLink>
                  ))}
                </Nav>
              </Navbar.Collapse>

              {/* Icons + menu toggle on the right */}
              <div className="d-flex align-items-center gap-3 ms-auto order-1 order-md-2">
                {/* Favorites */}
                <Link
                  to="/favorites"
                  className="text-color text-decoration-none bg-action action-icon-size rounded-5 position-relative d-flex justify-content-center align-items-center"
                  onClick={() => setExpanded(false)}
                >
                  <i className="fa-regular fa-heart action-color"></i>
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-danger fs-small">
                    {favorites.length}
                  </span>
                </Link>

                {/* Cart */}
                <Link
                  to="/cart"
                  className="text-color text-decoration-none bg-action action-icon-size rounded-5 position-relative d-flex justify-content-center align-items-center"
                  onClick={() => setExpanded(false)}
                >
                  <FaCartShopping className="action-color" />
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-danger fs-small">
                    {cart.length}
                  </span>
                </Link>

                {/* Menu button */}
                <Navbar.Toggle aria-controls="basic-navbar-nav" className="menu-button">
                  <FaBars className="custom-hamburger" />
                </Navbar.Toggle>
              </div>
            </Container>
          </Navbar>
        </section>
      )}
      {children}
      {shouldShowFooter && <CustomFooter />}
    </>
  );
};

export default Layout;